/**
 * The fetched weather for the boat's own position, for the Conditions card.
 *
 * Keyed by rounded position and model, so drifting round an anchorage does not
 * refetch and changing the forecast hour costs nothing: the whole three-day
 * hourly series is held and re-read at the new time. Same shape as the
 * real-weather field store, deliberately, so there is one pattern to follow.
 */
import { create } from 'zustand';
import {
  fetchLocalConditions,
  localConditionsAt,
  type LocalConditionFields,
  type LocalConditions,
} from '@/weather/localConditions';
import type { RealModelId } from '@/weather/realWeather';

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface LocalConditionsState {
  fields: LocalConditionFields | null;
  status: Status;
  error: string | null;
  key: string | null;
  /** Ensure the point forecast is loaded for this position + model. */
  load: (position: [number, number], modelId: RealModelId) => Promise<void>;
  /** Read the loaded forecast at an instant, or null if nothing is loaded. */
  at: (ms: number) => LocalConditions | null;
  clear: () => void;
}

/**
 * Two decimal places is about a mile. Close enough that a forecast fetched at one
 * end of an anchorage is the same forecast at the other, and coarse enough that
 * swinging to the tide all night is not a fetch a minute.
 */
function keyFor(position: [number, number], modelId: string): string {
  return `${modelId}:${position[0].toFixed(2)},${position[1].toFixed(2)}`;
}

export const useLocalConditionsStore = create<LocalConditionsState>((set, get) => ({
  fields: null,
  status: 'idle',
  error: null,
  key: null,

  load: async (position, modelId) => {
    const key = keyFor(position, modelId);
    const cur = get();
    if (cur.key === key && (cur.fields || cur.status === 'loading')) return;

    set({ key, status: 'loading', error: null });
    try {
      const fields = await fetchLocalConditions(position, modelId);
      // The view moved on while we waited: this answer is for somewhere else.
      if (get().key !== key) return;
      set({ fields, status: 'ready', error: null });
    } catch (e) {
      if (get().key !== key) return;
      set({ status: 'error', error: e instanceof Error ? e.message : 'Weather fetch failed' });
    }
  },

  at: (ms) => {
    const f = get().fields;
    return f ? localConditionsAt(f, ms) : null;
  },

  clear: () => set({ fields: null, status: 'idle', error: null, key: null }),
}));
